<template>
  <div
    class="p-inputgroup"
    :class="`formulate-input-element formulate-input-element--${context.class}`"
    :data-type="context.type"
  >
    <PrimeInputNumber
      :value="localAmount"
      @input="handleAmountInput"
      :mode="hasValidCurrency ? 'currency' : 'decimal'"
      :currency="localCurrencyCode"
      locale="en-US"
      :minFractionDigits="localMinFractionDigits"
      :maxFracionDigits="localMaxFractionDigits"
      v-bind="context.attributes"
      :disabled="amountDisabled"
      :placeholder="amountPlaceholder"
      @blur="context.blurHandler"
    />
    <!-- show disabled text input or button or nothing? if its a fixed currency -->
    <PrimeDropdown
      v-if="!hideCurrency && shouldHaveCurrencyOptions"
      key="currencyDropdown"
      :value="localCurrencyCode"
      @input="handleCurrencyInput"
      :options="localCurrencyOptions"
      :placeholder="currencyPlaceholder"
      :disabled="currencyDisabled"
    />
    <!-- <span 
            v-else-if="localCurrencyCode"
            key="currencyFixedOrDisabled"
            class="p-inputgroup-addon"
        >{{ localCurrencyCode }}</span>   don't show it if fixedCurrency -->
    <!-- optionLabel="currency" 
            optionValue="code"
            dataKey="code"  don't need these if we only use array of codes for value and label -->
  </div>
</template>

<script>
  import PrimeInputNumber from 'primevue/inputnumber';
  import PrimeDropdown from 'primevue/dropdown';
  import { tenantInfoMixin } from '../../js/custom-vue-mixins';

  // uses BootstrapVue textarea, but this is a custom Formulate input

  export default {
    name: 'FormulateMoney',
    components: {
      PrimeInputNumber,
      PrimeDropdown,
    },
    mixins: [tenantInfoMixin],
    props: {
      context: {
        // standard Formulate input context
        type: Object,
        required: true,
      },
      fixedCurrency: {
        // the currency code itself, if its a fixed one - can't rely on having it from value
        type: String,
        required: false, // NOT required, so we can use this for fixed or variable currency
      },
      currencyOptions: {
        type: Array,
        required: false, // if not provided, and not fixedCurrency, then use options from tenant settings?
      },
      decimalPlaces: {
        type: Number,
        required: false,
      },
      maxDigits: {
        // TODO - start using this -- backend has it
        type: Number,
        required: false,
      },
      hideCurrency: {
        type: Boolean,
        required: false,
        default: false,
      },
      // all other props, just pass them on to textarea in context.attributes
    },
    data() {
      return {
        // SNIPPETS
        localAmount: null,
        localCurrencyCode: undefined,
        modelState: 'null', // new variable to track state: 'null', 'currencySelected', 'amountEntered', 'reset'
      };
    },
    computed: {
      hasValidAmount() {
        return this.localAmount !== null && this.localAmount !== undefined;
      },
      hasValidCurrency() {
        // used to change mode to non-currency if we don't have one... or else it throws error
        // must also check that localCurrencyCode === fixedCurrency if its fixed
        if (!this.shouldHaveCurrencyOptions) {
          return !!this.localCurrencyCode && (this.fixedCurrency ? this.fixedCurrency === this.localCurrencyCode : true); // no options? then just see if we have something
        }
        // if we ARE supposed to have options, check if we have truthy value AND that its one of the options
        return !!this.localCurrencyCode && this.localCurrencyOptions.includes(this.localCurrencyCode);
      },
      shouldHaveCurrencyOptions() {
        if (this.isDisabled) {
          return false;
        }
        return !this.fixedCurrency;
      },
      localCurrencyOptions() {
        return this.currencyOptions && Array.isArray(this.currencyOptions) ? [...this.currencyOptions] : this.getDefaultCurrencyOptions(); // get from tenantInfoMixin?
      },
      localMinFractionDigits() {
        return this.decimalPlaces ?? undefined; // may not need this...
      },
      localMaxFractionDigits() {
        return this.decimalPlaces ?? undefined; // may not need this...
      },
      isDisabled() {
        return this.context.attributes?.disabled ?? false;
      },
      amountDisabled() {
        return this.isDisabled || !this.localCurrencyCode; // force selecting currencyCode before entering amount!
      },
      currencyDisabled() {
        return this.isDisabled;
      },
      amountPlaceholder() {
        if (this.isDisabled) {
          return '---';
        }
        return !this.localCurrencyCode ? 'Select currency...' : 'Enter amount...';
      },
      currencyPlaceholder() {
        return '---';
      },
    },
    watch: {
      'context.model': {
        deep: true, // since value here is nested object
        handler: function (newValueObj) {
          if (!newValueObj) {
            // Model starts as null, set local state to 'null'
            this.localAmount = null;
            this.localCurrencyCode = this.fixedCurrency ? this.fixedCurrency : undefined;
            this.modelState = 'null';
          } else {
            // Update with existing values, and set local state accordingly
            if (this.fixedCurrency && newValueObj.currency && this.fixedCurrency !== newValueObj.currency) {
              throw new Error(`Currency mismatch! Should have fixedCurrency of ${this.fixedCurrency}, but passed in value with currency ${newValueObj.currency}`);
            }
            this.localAmount = newValueObj.amount ?? null;
            this.localCurrencyCode = this.fixedCurrency ? this.fixedCurrency : newValueObj.currency;

            // Set state based on the presence of amount and currency
            if (this.localAmount && this.localCurrencyCode) {
              this.modelState = 'amountEntered';
            } else if (this.localCurrencyCode) {
              this.modelState = 'currencySelected';
            } else {
              this.modelState = 'null';
            }
          }
        },
      },
    },
    mounted() {},
    methods: {
      handleAmountInput(amountValue) {
        this.localAmount = amountValue;

        if (!this.hasValidAmount) {
          this.modelState = 'reset'; // If the amount is cleared, reset the state
        } else if (this.localCurrencyCode) {
          this.modelState = 'amountEntered'; // If both amount and currency are valid, set state to amountEntered
        }

        this.updateModel();
      },
      handleCurrencyInput(currencyCode) {
        this.localCurrencyCode = currencyCode;

        if (!this.localAmount) {
          this.modelState = 'currencySelected'; // Currency selected but no amount
        } else {
          this.modelState = 'amountEntered'; // Both currency and amount selected
        }

        this.updateModel();
      },
      updateModel() {
        switch (this.modelState) {
          case 'null':
            // Initial state, set model to null
            this.context.model = null;
            break;

          case 'currencySelected':
            // Only the currency is selected, so send the model with currency and no amount
            if (this.hasValidAmount) {
              this.context.model = { amount: this.localAmount, currency: this.localCurrencyCode };
            }
            break;

          case 'amountEntered':
            // Both amount and currency are entered, update the model
            if (this.hasValidCurrency && this.localAmount !== null && this.localAmount !== undefined) {
              this.context.model = { amount: this.localAmount, currency: this.localCurrencyCode };
            }
            break;

          case 'reset':
            // Reset the model to null if the amount is cleared
            this.context.model = null;
            this.localAmount = null;
            break;

          default:
            // Fallback if we hit an undefined state
            this.context.model = null;
        }
      },
      getDefaultCurrencyOptions() {
        // we get a flat list of just the currency codes from tenantInfoMixin
        // currencyChoices is array of objects with { text, value } - value is the code
        return this.$tenantInfo?.settings?.currency_settings?.currencyChoices?.map((ch) => ch.value) ?? [];
      },
    },
  };
</script>

<style></style>
